import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { TabsContent } from "../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Plus, Edit2, Trash2, Power, PowerOff, Scale } from "lucide-react";

/**
 * RulesTab — Pestaña "Reglas de Negocio" del catálogo (Package Builder).
 *
 * Extraída 1:1 desde `pages/CatalogPage.js`. Muestra los filtros por tipo de regla
 * (Todas / Exclusividad / Requiere Categoría / Descuento / Bloqueo) y la tabla de reglas
 * ya filtradas; permite crear, editar, activar/desactivar y eliminar. Su visibilidad la
 * controla el padre mediante `canManageRules` (SUPERADMIN / GESTION).
 *
 * @param {string}   props.ruleTypeFilter        Filtro de tipo activo ("all" | tipo de regla).
 * @param {Function} props.setRuleTypeFilter     Cambia el filtro de tipo.
 * @param {Array}    props.filteredRules         Reglas ya filtradas por `ruleTypeFilter`.
 * @param {Function} props.setRuleModalOpen      Abre el modal de "Nueva Regla".
 * @param {Function} props.setEditingRule        Abre el modal de edición con la regla dada.
 * @param {Function} props.getRuleTypeBadgeClass (tipo) => string — clases del badge según tipo.
 * @param {Function} props.handleToggleRule      (rule) => void — activa/desactiva la regla.
 * @param {Function} props.canDelete             (resource) => boolean — permiso RBAC para eliminar.
 * @param {Function} props.handleDeleteRule      (id) => void — elimina una regla.
 */
export const RulesTab = ({
  ruleTypeFilter,
  setRuleTypeFilter,
  filteredRules,
  setRuleModalOpen,
  setEditingRule,
  getRuleTypeBadgeClass,
  handleToggleRule,
  canDelete,
  handleDeleteRule,
}) => {
  return (
          <TabsContent value="rules" className="mt-6">
            <Card className="bg-[#0F0F3D] border-[#1E1E5F]">
              <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle className="text-lg text-white">Reglas de Negocio</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={ruleTypeFilter === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setRuleTypeFilter("all")}
                    className={ruleTypeFilter === "all" ? "bg-[#42E695] text-[#04042C]" : "border-[#1E1E5F] text-slate-300"}
                  >
                    Todas
                  </Button>
                  <Button
                    variant={ruleTypeFilter === "EXCLUSIVIDAD" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setRuleTypeFilter("EXCLUSIVIDAD")}
                    className={ruleTypeFilter === "EXCLUSIVIDAD" ? "bg-orange-500" : "border-[#1E1E5F] text-slate-300"}
                  >
                    Exclusividad
                  </Button>
                  <Button
                    variant={ruleTypeFilter === "REQUIERE_CATEGORIA" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setRuleTypeFilter("REQUIERE_CATEGORIA")}
                    className={ruleTypeFilter === "REQUIERE_CATEGORIA" ? "bg-blue-500" : "border-[#1E1E5F] text-slate-300"}
                  >
                    Requiere Categoría
                  </Button>
                  <Button
                    variant={ruleTypeFilter === "DESCUENTO_CONDICIONAL" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setRuleTypeFilter("DESCUENTO_CONDICIONAL")}
                    className={ruleTypeFilter === "DESCUENTO_CONDICIONAL" ? "bg-purple-500" : "border-[#1E1E5F] text-slate-300"}
                  >
                    Descuento
                  </Button>
                  <Button
                    variant={ruleTypeFilter === "BLOQUEO_STANDALONE" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setRuleTypeFilter("BLOQUEO_STANDALONE")}
                    className={ruleTypeFilter === "BLOQUEO_STANDALONE" ? "bg-red-500" : "border-[#1E1E5F] text-slate-300"}
                  >
                    Bloqueo
                  </Button>
                </div>
                <Button
                  onClick={() => setRuleModalOpen(true)}
                  className="bg-[#42E695] text-[#04042C]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Regla
                </Button>
              </CardHeader>
              <CardContent>
                {filteredRules.length === 0 ? (
                  <div className="text-center py-8">
                    <Scale className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                    <p className="text-slate-400">No hay reglas configuradas</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#1E1E5F]">
                        <TableHead className="text-[#03BEE4]">Nombre</TableHead>
                        <TableHead className="text-[#03BEE4]">Tipo</TableHead>
                        <TableHead className="text-[#03BEE4]">Descripción</TableHead>
                        <TableHead className="text-[#03BEE4]">Estado</TableHead>
                        <TableHead className="text-[#03BEE4]">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRules.map((rule) => (
                        <TableRow key={rule.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]">
                          <TableCell className="font-medium text-white">{rule.nombre}</TableCell>
                          <TableCell>
                            <Badge className={getRuleTypeBadgeClass(rule.tipo)}>
                              {rule.tipo.replace(/_/g, " ")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300 max-w-xs truncate">
                            {rule.descripcion || rule.mensaje_error}
                          </TableCell>
                          <TableCell>
                            <Badge className={rule.is_active ? "bg-[#42E695]/20 text-[#42E695]" : "bg-slate-500/20 text-slate-400"}>
                              {rule.is_active ? "Activa" : "Inactiva"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingRule(rule)}
                              >
                                <Edit2 className="h-4 w-4 text-[#03BEE4]" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleRule(rule)}
                              >
                                {rule.is_active ? (
                                  <PowerOff className="h-4 w-4 text-orange-400" />
                                ) : (
                                  <Power className="h-4 w-4 text-[#42E695]" />
                                )}
                              </Button>
                              {canDelete("catalog") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRule(rule.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-400" />
                              </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
  );
};
